// Save states of EmulatorJS games, in their own IndexedDB database, named
// like mGBA's (<base>.ssN) so slots work the same for every engine. They
// stay in this browser: an N64 state is far past the cloud's 2 MB
// save limit.

const DB_NAME = "pocket-play-ejs-states";
const STORE = "states";

export interface StoredState {
  bytes: Uint8Array;
  /** Epoch ms when it was written. */
  at: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

export const ejsStates = {
  get: (file: string) => withStore<StoredState | undefined>("readonly", (s) => s.get(file)),
  put: (file: string, bytes: Uint8Array) =>
    withStore("readwrite", (s) => s.put({ bytes, at: Date.now() } satisfies StoredState, file)).then(() => undefined),
  remove: (file: string) => withStore("readwrite", (s) => s.delete(file)).then(() => undefined),
  /** File names of every stored state. */
  list: async () => (await withStore("readonly", (s) => s.getAllKeys())).map(String),
  /** Moves a state to another name unless that name already has one. */
  async move(from: string, to: string) {
    const state = await ejsStates.get(from);
    if (!state || (await ejsStates.get(to))) return;
    await withStore("readwrite", (s) => s.put(state, to));
    await ejsStates.remove(from);
  },
};
